import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionOutcome } from '@hooks/useSessionOutcome';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { Spin, Row, Button, Alert, Tooltip } from 'antd';
import { ReloadOutlined } from 'antd/icons';
import * as actions from '@actions';
import { PDFDocument } from 'pdf-lib';
import { useAdobeClientId } from 'vl-common/src/hooks/useAdobeClientId';
import { css } from '@emotion/react';
import { useDebouncedCallback } from '@mantine/hooks';
import { errorModal } from 'vl-common/src/components/Modals';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { LetterTypesFormItem } from './Components/LetterTypesFormItem';
import useIsSessionSaving from './hooks/useIsSessionSaving';
import { useHeidiSession } from '@components/Clinicians/Session/hooks/useHeidiSession';
import { useHeidiEnabled } from '@hooks/useHeidiEnabled';

interface LetterGenerationProps {
  className?: string;
}

const LetterGeneration: React.FC<LetterGenerationProps> = ({ className = '' }) => {
  const { t } = useTranslation('common');
  const { appointment } = useSessionAppointment();
  const appointmentId = useSessionAppointmentId();
  const isCompleted = appointment.appointment_completed;
  const refetchLetter = useRef(async () => {});
  const dispatch = useDispatch();
  const classNames = cx('letter-generation', className);
  const [pdfDocument, setPdfDocument] = useState<Uint8Array | null>(null);
  const [pdfRendered, setPdfRendered] = useState(false);
  const [, setAlert] = useSessionFormState('alert', null);
  const adobeClientId = useAdobeClientId();
  const { appointmentVersion } = useSessionOutcome();
  const [letterViewed, setLetterViewed] = useSessionFormState('letterViewed', false);
  const [isLetterReadToBottom, setIsLetterReadToBottom] = useSessionFormState('isLetterReadToBottom', false);
  const [isLetterAccuracyConfirmed, setIsLetterAccuracyConfirmed] = useSessionFormState(
    'isLetterAccuracyConfirmed',
    false
  );
  const [letterReq, setLetterReq] = useSessionFormState('letterReq', {
    letter_code: '',
    letter_template_type: ''
  });
  const [, setAppointmentOutcome] = useSessionFormState('appointment_outcome');
  const { isSessionSavePending } = useIsSessionSaving();

  const isAuditAppointment = appointment?.appt_type_code === 'AUDIT';

  const heidiEnabled = useHeidiEnabled(appointmentId);
  const { status: heidiStatus, hasHeidiError } = useHeidiSession();
  const isHeidiActive = heidiEnabled && heidiStatus !== 'idle' && !hasHeidiError;
  const isAiConfirmationRequired = isHeidiActive && !isAuditAppointment && !isCompleted;

  // Refs for non-reactive state and the Adobe instance
  const totalPagesRef = useRef<number>(0);
  const adobeViewRef = useRef<any>(null);
  const scrollSensorRef = useRef<HTMLDivElement | null>(null);

  const sessionAppointmentOutcome = appointment?.appointment_outcome_code;

  // Moved From Sessions.tsx
  const { data: letterFiles, isLoading: isLetterFilesLoading } = useQuery({
    queryKey: ['letterFiles', appointmentId, isCompleted],
    queryFn: () =>
      dispatch(
        actions.getLetterFiles({
          appt_id: appointmentId,
          page: 1,
          case_id: appointment.case_id
        })
      ),
    select: (data) => data.payload
  });

  const { data: letterCodes, isLoading: isLetterCodesLoading } = useQuery({
    queryKey: ['letterCodes', appointmentId],
    queryFn: () => dispatch(actions.getLetterCodes(appointmentId)),
    select: (data) => data.letter_types
  });

  const { mutateAsync: saveLamdaPDF, isPending: pdfIsLoading } = useMutation({
    mutationFn: async (preview: boolean) =>
      dispatch(
        actions.saveLamdaPDF(appointment.clinician_user_guid, appointmentId, appointment.patient_user_guid, {
          preview,
          ...letterReq
        })
      )
  });

  const isLetterGenerating = pdfIsLoading || isLetterCodesLoading || isLetterFilesLoading || isSessionSavePending;

  const defaultLetterCode = letterCodes?.[0].letter_code;

  useEffect(() => {
    if (!defaultLetterCode || !letterCodes) return;

    const savedLetter =
      letterFiles?.find(
        (file: { appt_id: number; document_type: string }) => file.appt_id === appointment.appointment_id
      ) || letterFiles?.find((file: { appt_id: number; document_type: string }) => file.document_type === 'Letter');
    const savedLetterCode = letterCodes?.find((letter) => letter.letter_code === savedLetter?.letter_type)?.letter_code;

    if (letterReq?.letter_code === '' || isCompleted) {
      setLetterReq({
        letter_code: savedLetterCode || defaultLetterCode,
        letter_template_type: ''
      });
    }
  }, [
    appointment.appointment_id,
    defaultLetterCode,
    dispatch,
    isCompleted,
    letterCodes,
    letterFiles,
    letterReq,
    setLetterReq
  ]);
  const fetchPDFLetter = useCallback(
    async (isPreview = true) => {
      setPdfRendered(false);
      setPdfDocument(null);
      if (!isCompleted) {
        setIsLetterReadToBottom(false);
        setIsLetterAccuracyConfirmed(false);
      }

      const response = await saveLamdaPDF(isPreview);
      const newPDFDoc = await PDFDocument.load(response.pdf);
      totalPagesRef.current = newPDFDoc.getPageCount();

      const pdfBytes = await newPDFDoc.save();
      setPdfDocument(pdfBytes);
    },
    [saveLamdaPDF, isCompleted]
  );

  const debouncedPdfFetch = useDebouncedCallback((preview: boolean) => {
    fetchPDFLetter(preview);
  }, 600);

  useEffect(() => {
    refetchLetter.current = fetchPDFLetter;
  }, [letterReq, fetchPDFLetter, isSessionSavePending]);

  useEffect(() => {
    if (sessionAppointmentOutcome) {
      setAppointmentOutcome(sessionAppointmentOutcome);
    }
  }, [sessionAppointmentOutcome]);

  useEffect(() => {
    if (!letterViewed) {
      setLetterViewed(true);
      setAlert(null);
    }
  }, [letterViewed, setLetterViewed, setAlert]);

  useEffect(() => {
    if (letterReq && letterReq?.letter_code !== '') {
      // Debounce initial call to fetch PDF to ensure all values are up to date
      debouncedPdfFetch(true);
    }
  }, [letterReq, debouncedPdfFetch]);

  useEffect(() => {
    if (isSessionSavePending && !isCompleted) {
      setIsLetterAccuracyConfirmed(false);
    }
  }, [isSessionSavePending, isCompleted]);

  useEffect(() => {
    if (isCompleted) {
      setIsLetterAccuracyConfirmed(true);
    }
  }, [isCompleted]);

  useEffect(() => {
    if (!pdfDocument) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsLetterReadToBottom(true);
        }
      },
      { threshold: 0.5 }
    );

    if (scrollSensorRef.current) {
      observer.observe(scrollSensorRef.current);
    }

    const initAdobeDC = () => {
      if (!adobeClientId) {
        errorModal({
          title: 'Invalid API Key',
          content: `The Adobe API key for HBSUK could not be identified: ${window.location.hostname}`
        });
        return;
      }
      const container = document.getElementById('adobe-dc-preview-2');
      if (container) container.innerHTML = '';

      const adobeDCView = new (window as any).AdobeDC.View({
        clientId: adobeClientId,
        divId: 'adobe-dc-preview-2',
        downloadWithCredentials: true
      });

      adobeViewRef.current = adobeDCView;

      adobeDCView.registerCallback(
        (window as any).AdobeDC.View.Enum.CallbackType.EVENT_LISTENER,
        (event: any) => {
          if (event.type === 'APP_RENDERING_DONE') {
            setPdfRendered(true);
            const total = totalPagesRef.current;
            if (total <= 1) setIsLetterReadToBottom(true);
          }
        },
        { listenOn: [(window as any).AdobeDC.View.Enum.Events.APP_RENDERING_DONE] }
      );

      adobeDCView.previewFile(
        {
          content: { promise: Promise.resolve(pdfDocument) },
          metaData: { fileName: `${letterReq?.letter_code}` }
        },
        {
          enableFormFilling: !isCompleted,
          showLeftHandPanel: false,
          showDownloadPDF: false,
          showPrintPDF: false,
          showAnnotationTools: false,
          defaultViewMode: 'FIT_WIDTH',
          embedMode: 'SIZED_CONTAINER'
        }
      );
      adobeDCView.registerCallback(
        window.AdobeDC.View.Enum.CallbackType.SAVE_API,
        async (metaData: any, content: any) => {
          const base64 = btoa(new Uint8Array(content).reduce((data, byte) => data + String.fromCharCode(byte), ''));
          const currentPDFState = {
            appt_id: appointment.appointment_id.toString(),
            response_json: null,
            base64
          };

          // Change to post
          await dispatch(actions.postPDFState(appointment.appointment_id, undefined, currentPDFState));

          /* Add your custom save implementation here...and based on that resolve or reject response in given format */
          return new Promise((resolve) => {
            resolve({
              code: window.AdobeDC.View.Enum.ApiResponseCode.SUCCESS,
              data: {
                /* Updated file metadata after successful save operation */
                metaData
              }
            });
          });
        }
      );
    };

    if ((window as any).AdobeDC) {
      initAdobeDC();
    } else {
      document.addEventListener('adobe_dc_view_sdk.ready', initAdobeDC);
    }

    return () => {
      observer.disconnect();
      document.removeEventListener('adobe_dc_view_sdk.ready', initAdobeDC);
    };
  }, [pdfDocument, adobeClientId, isCompleted, letterReq?.letter_code]);

  return (
    <div
      className={`letter ${classNames}`}
      css={css`
        position: relative;
        width: 100%;
        min-height: 200px;
      `}
    >
      {isLetterGenerating && (
        <Spin
          size="large"
          tip="Fetching letter..."
          style={{ position: 'absolute', top: '50%', left: '50%', zIndex: 10 }}
        />
      )}

      {appointmentVersion && appointmentVersion >= 2 && (
        <div
          css={css`
            padding: 0 11px 0 20px;
          `}
        >
          <LetterTypesFormItem />
        </div>
      )}

      {isAiConfirmationRequired && !isLetterAccuracyConfirmed && (
        <div
          style={{
            margin: '10px 20px',
            padding: '12px 16px',
            backgroundColor: '#f0f7ff',
            border: '1px solid #adc6ff',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <div
            style={{
              backgroundColor: '#597ef7',
              borderRadius: '50%',
              width: '22px',
              height: '22px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold', fontFamily: 'serif' }}>i</span>
          </div>

          <span style={{ fontSize: '14px', color: '#000', lineHeight: '1.5' }}>
            <strong style={{ marginRight: '4px' }}>{t('Action Required:')}</strong>
            {t('Please read the letter in full and scroll to the bottom to enable confirmation.')}
          </span>
        </div>
      )}

      <Row style={{ marginBottom: 20 }} justify="end" align="middle">
        {t("Can't see your changes? Please refresh here")}
        <Button
          disabled={!pdfDocument}
          style={{ margin: '0 12px 0 8px' }}
          onClick={() => fetchPDFLetter()}
          icon={<ReloadOutlined />}
          size="small"
          data-testid="refresh-letter"
        />
        {pdfRendered && <span data-testid="pdf-rendered" />}
      </Row>

      {!isLetterGenerating && !pdfDocument && letterReq?.letter_code === '' && (
        <Alert
          style={{ margin: '20px' }}
          message={t('Unable to retrieve letter')}
          description={t('There was an error retrieving the PDF letter or letter codes. Please contact support.')}
          type="error"
          showIcon
        />
      )}

      <div
        style={{
          visibility: isLetterGenerating ? 'hidden' : 'visible',
          height: 'max(calc(100svh - 300px), 650px)',
          overflowY: 'auto',
          position: 'relative',
          border: '1px solid #eee'
        }}
      >
        <div id="adobe-dc-preview-2" style={{ height: '1200px', width: '100%' }} />
        <div
          ref={scrollSensorRef}
          style={{
            height: '100px',
            width: '100%',
            marginTop: '-50px',
            background: 'transparent',
            pointerEvents: 'none'
          }}
        />
      </div>

      {pdfDocument && !isLetterGenerating && isAiConfirmationRequired && (
        <div
          style={{
            marginTop: '24px',
            border: '1px solid #d9d9d9',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#fff'
          }}
        >
          <div
            style={{
              padding: '12px 20px',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fff'
            }}
          >
            <span style={{ fontWeight: 600, fontSize: '14px', color: '#262626' }}>
              {t('Clinical Responsibility Confirmation')}
            </span>
          </div>

          <div style={{ padding: '20px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                cursor: isLetterReadToBottom ? 'pointer' : 'not-allowed'
              }}
            >
              <Tooltip
                title={!isLetterReadToBottom ? t('Please scroll to the bottom of the PDF to enable confirmation') : ''}
                trigger={['hover', 'focus']}
              >
                <span style={{ display: 'inline-flex', cursor: !isLetterReadToBottom ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="checkbox"
                    disabled={!isLetterReadToBottom}
                    checked={isLetterAccuracyConfirmed}
                    onChange={(e) => setIsLetterAccuracyConfirmed(e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      marginTop: '3px',
                      cursor: isLetterReadToBottom ? 'pointer' : 'not-allowed',
                      pointerEvents: !isLetterReadToBottom ? 'none' : 'auto'
                    }}
                  />
                </span>
              </Tooltip>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span
                  style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: isLetterReadToBottom ? '#262626' : '#bfbfbf',
                    lineHeight: '1.4'
                  }}
                >
                  {isLetterReadToBottom
                    ? t(
                        'I confirm that I have reviewed this letter in its entirety and accept clinical responsibility for its accuracy.'
                      )
                    : t('Please scroll to the bottom of the PDF to enable confirmation.')}
                </span>

                <span
                  style={{
                    fontSize: '13px',
                    color: isLetterReadToBottom ? '#595959' : '#bfbfbf',
                    lineHeight: '1.6'
                  }}
                >
                  {t(
                    'I understand that as the treating clinician, I am legally and professionally responsible for ensuring all information in this letter is accurate, appropriate, and reflects the clinical encounter. This includes verifying all AI-generated content and ensuring it meets the required medical and legal standards for patient correspondence.'
                  )}
                </span>
              </div>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterGeneration;
